import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { 
  Bot, Phone, MessageCircle, Calendar, Clock, Users, 
  Play, RefreshCw, TrendingUp, CheckCircle, XCircle,
  PhoneCall, BarChart3, Send, Zap, Headphones, FileText,
  Eye, Trash2, Volume2, MessageSquare, ChevronRight, Search, X
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to parse AI insights which might be JSON strings
const parseAIInsights = (aiInsights, huskyAnalytics) => {
  let summary = huskyAnalytics?.summary || '';
  let sentiment = huskyAnalytics?.sentiment || '';
  let interestLevel = huskyAnalytics?.interest_level || '';
  let nextSteps = huskyAnalytics?.next_steps || '';
  
  if (aiInsights?.call_summary) {
    try {
      const cs = aiInsights.call_summary;
      if (typeof cs === 'string' && cs.trim().startsWith('{')) {
        const parsed = JSON.parse(cs);
        summary = parsed.call_summary || summary;
        sentiment = parsed.customer_sentiment || sentiment;
        interestLevel = parsed.interest_level || interestLevel;
        nextSteps = parsed.next_best_action || nextSteps;
      } else if (typeof cs === 'string') {
        summary = cs;
      }
    } catch {
      // If parsing fails, use as-is but truncate if too long
      summary = typeof aiInsights.call_summary === 'string' 
        ? aiInsights.call_summary.slice(0, 300) + (aiInsights.call_summary.length > 300 ? '...' : '')
        : '';
    }
  }
  
  return { summary, sentiment, interestLevel, nextSteps };
};

export default function AIFollowupPage({ user, logout }) {
  const [systemStatus, setSystemStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [detailedActivity, setDetailedActivity] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerLeadId, setTriggerLeadId] = useState('');
  const [triggerResult, setTriggerResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadHistory, setLeadHistory] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, statsRes, dashRes, callsRes, callLogsRes, waLogsRes, activityRes, recordingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-followup/status`),
        axios.get(`${API_URL}/api/ai-followup/stats`),
        axios.get(`${API_URL}/api/ai-followup/dashboard`),
        axios.get(`${API_URL}/api/ai-followup/scheduled-calls?status=scheduled`),
        axios.get(`${API_URL}/api/ai-followup/call-logs?limit=50`),
        axios.get(`${API_URL}/api/ai-followup/whatsapp-logs?limit=50`),
        axios.get(`${API_URL}/api/ai-followup/detailed-activity?limit=30`),
        axios.get(`${API_URL}/api/ai-followup/recordings/recent?limit=20`)
      ]);
      
      setSystemStatus(statusRes.data);
      setStats(statsRes.data);
      setDashboard(dashRes.data);
      setScheduledCalls(callsRes.data.calls || []);
      setCallLogs(callLogsRes.data.calls || []);
      setWhatsappLogs(waLogsRes.data.messages || waLogsRes.data.logs || []);
      setDetailedActivity(activityRes.data.activity || []);
      setRecordings(recordingsRes.data.recordings || []);
    } catch (error) {
      console.error('Error fetching AI data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const triggerFollowup = async () => {
    if (!triggerLeadId) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-followup/trigger/${triggerLeadId}?force=true`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to trigger' });
    } finally {
      setIsProcessing(false);
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('This will delete all test leads (phone starting with 99999). Continue?')) return;
    setIsProcessing(true);
    try {
      const res = await axios.delete(`${API_URL}/api/ai-followup/cleanup-test-data`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to cleanup' });
    } finally {
      setIsProcessing(false);
    }
  };

  const cleanupOldCalls = async () => {
    if (!window.confirm('This will delete all scheduled/triggered calls older than 7 days. Continue?')) return;
    setIsProcessing(true);
    try {
      const res = await axios.delete(`${API_URL}/api/ai-followup/scheduled-calls/cleanup?older_than_days=7`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to cleanup old calls' });
    } finally {
      setIsProcessing(false);
    }
  };

  const updateStaleCalls = async () => {
    if (!window.confirm('This will sync all triggered calls with HuskyVoice and update their status. Continue?')) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-followup/scheduled-calls/update-stale`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to update stale calls' });
    } finally {
      setIsProcessing(false);
    }
  };

  const syncHuskyVoice = async () => {
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-followup/sync-huskyvoice-recordings`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to sync HuskyVoice' });
    } finally {
      setIsProcessing(false);
    }
  };

  const transcribeAllRecordings = async () => {
    if (!window.confirm('This will transcribe all recordings without transcripts using Whisper AI. Continue?')) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-followup/recordings/transcribe-all?limit=10`);
      setTriggerResult(res.data);
      fetchData();
    } catch (error) {
      setTriggerResult({ error: error.response?.data?.detail || 'Failed to transcribe recordings' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      // Search in leads
      const leadsRes = await axios.get(`${API_URL}/api/leads?search=${encodeURIComponent(searchQuery)}&limit=20`, {
        withCredentials: true
      });
      
      // Filter call logs and recordings by phone/name
      const query = searchQuery.toLowerCase();
      const filteredCalls = callLogs.filter(c => 
        c.phone?.includes(query) || 
        c.lead_name?.toLowerCase().includes(query) ||
        c.lead_id?.toLowerCase().includes(query)
      );
      const filteredRecordings = recordings.filter(r => 
        r.phone?.includes(query) || 
        r.lead_name?.toLowerCase().includes(query) ||
        r.call_id?.toLowerCase().includes(query)
      );
      const filteredWhatsapp = whatsappLogs.filter(w => 
        w.phone?.includes(query) || 
        w.lead_name?.toLowerCase().includes(query)
      );
      
      setSearchResults({
        leads: leadsRes.data.leads || [],
        calls: filteredCalls,
        recordings: filteredRecordings,
        whatsapp: filteredWhatsapp,
        total: (leadsRes.data.leads?.length || 0) + filteredCalls.length + filteredRecordings.length
      });
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({ leads: [], calls: [], recordings: [], whatsapp: [], total: 0, error: true });
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const deleteScheduledCall = async (callId) => {
    if (!window.confirm('Delete this scheduled call?')) return;
    try {
      await axios.delete(`${API_URL}/api/ai-followup/scheduled-calls/${callId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting call:', error);
    }
  };

  const viewLeadHistory = async (leadId) => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-followup/lead/${leadId}/full-history`);
      setLeadHistory(res.data);
      setShowLeadModal(true);
    } catch (error) {
      console.error('Error fetching lead history:', error);
    }
  };

  const viewCallDetails = async (call) => {
    // If we have lead_id, fetch full history including transcript
    if (call.lead_id) {
      try {
        const res = await axios.get(`${API_URL}/api/ai-followup/lead/${call.lead_id}/full-history`);
        setSelectedCall({ ...call, fullHistory: res.data });
      } catch (error) {
        setSelectedCall(call);
      }
    } else {
      setSelectedCall(call);
    }
    setShowCallModal(true);
  };

  const viewMessageDetails = async (message) => {
    // If we have lead_id, fetch lead info
    if (message.lead_id) {
      try {
        const res = await axios.get(`${API_URL}/api/ai-followup/lead/${message.lead_id}/full-history`);
        setSelectedMessage({ ...message, leadInfo: res.data.lead });
      } catch (error) {
        setSelectedMessage(message);
      }
    } else {
      setSelectedMessage(message);
    }
    setShowMessageModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { color: 'bg-blue-500', label: 'New' },
      'whatsapp_sent': { color: 'bg-yellow-500', label: 'WhatsApp Sent' },
      'whatsapp_responded': { color: 'bg-green-500', label: 'Responded' },
      'scheduled': { color: 'bg-purple-500', label: 'Scheduled' },
      'triggered': { color: 'bg-orange-500', label: 'Triggered' },
      'completed': { color: 'bg-green-600', label: 'Completed' },
      'failed': { color: 'bg-red-500', label: 'Failed' },
      'appointment_booked': { color: 'bg-emerald-500', label: 'Booked' },
      'not_interested': { color: 'bg-gray-500', label: 'Not Interested' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-400', label: status || 'Unknown' };
    return <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>;
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    return `****${cleaned.slice(-4)}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, h:mm a');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="ai-followup-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="w-7 h-7 text-purple-600" />
              AI Lead Follow-up System
            </h1>
            <p className="text-gray-500 mt-1">
              WhatsApp + Voice AI • Real-time call recordings & transcriptions
            </p>
          </div>
          <div className="flex items-center gap-2">
            {systemStatus?.system_initialized ? (
              <Badge className="bg-green-500 text-white">
                <CheckCircle className="w-3 h-3 mr-1" /> System Active
              </Badge>
            ) : (
              <Badge className="bg-red-500 text-white">
                <XCircle className="w-3 h-3 mr-1" /> System Offline
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-white">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, phone, or lead ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 pr-10"
                  data-testid="ai-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="ai-search-btn"
              >
                {isSearching ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" /> Search
                  </>
                )}
              </Button>
            </div>
            
            {/* Search Results */}
            {searchResults && (
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">
                    Search Results: {searchResults.total} found for "{searchQuery}"
                  </h3>
                  <Button variant="ghost" size="sm" onClick={clearSearch}>
                    Clear Search
                  </Button>
                </div>
                
                {searchResults.total === 0 ? (
                  <p className="text-gray-500 text-center py-4">No results found</p>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Leads Results */}
                    {searchResults.leads?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-purple-600 mb-2 flex items-center gap-1">
                          <Users className="w-4 h-4" /> Leads ({searchResults.leads.length})
                        </h4>
                        <div className="grid gap-2">
                          {searchResults.leads.slice(0, 5).map((lead) => (
                            <div 
                              key={lead.lead_id} 
                              className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-purple-300 cursor-pointer"
                              onClick={() => viewLeadHistory(lead.lead_id)}
                            >
                              <div>
                                <p className="font-medium text-gray-900">{lead.name}</p>
                                <p className="text-sm text-gray-500">{lead.phone} • {lead.city || 'No city'}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(lead.ai_followup_status || lead.disposition)}
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Call Logs Results */}
                    {searchResults.calls?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-orange-600 mb-2 flex items-center gap-1">
                          <PhoneCall className="w-4 h-4" /> Voice Calls ({searchResults.calls.length})
                        </h4>
                        <div className="grid gap-2">
                          {searchResults.calls.slice(0, 5).map((call, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-orange-300 cursor-pointer"
                              onClick={() => call.lead_id && viewLeadHistory(call.lead_id)}
                            >
                              <div>
                                <p className="font-medium text-gray-900">{call.lead_name || 'Unknown'}</p>
                                <p className="text-sm text-gray-500">{call.phone} • {formatDateTime(call.triggered_at)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(call.status)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Recordings Results */}
                    {searchResults.recordings?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-green-600 mb-2 flex items-center gap-1">
                          <Headphones className="w-4 h-4" /> Recordings ({searchResults.recordings.length})
                        </h4>
                        <div className="grid gap-2">
                          {searchResults.recordings.slice(0, 5).map((rec, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-green-300"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{rec.lead_name || rec.phone || 'Unknown'}</p>
                                <p className="text-sm text-gray-500">
                                  Duration: {rec.duration || 0}s • {formatDateTime(rec.created_at)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {rec.recording_url && (
                                  <a 
                                    href={rec.recording_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800"
                                  >
                                    <Play className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">Total Processed</p>
                  <p className="text-2xl font-bold text-blue-900">{stats?.total_processed || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-600 font-medium">Today</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats?.today_processed || 0}</p>
                </div>
                <Zap className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-medium">WhatsApp Sent</p>
                  <p className="text-2xl font-bold text-green-900">{whatsappLogs.length || 0}</p>
                </div>
                <MessageCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600 font-medium">Calls Made</p>
                  <p className="text-2xl font-bold text-purple-900">{callLogs.filter(c => c.status === 'triggered' || c.status === 'completed').length}</p>
                </div>
                <PhoneCall className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-orange-600 font-medium">Pending Calls</p>
                  <p className="text-2xl font-bold text-orange-900">{scheduledCalls.length}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Recordings</p>
                  <p className="text-2xl font-bold text-emerald-900">{recordings.length}</p>
                </div>
                <Headphones className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="mb-4 bg-gray-100">
            <TabsTrigger value="activity" className="data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-1" /> Activity
            </TabsTrigger>
            <TabsTrigger value="calls" className="data-[state=active]:bg-white">
              <PhoneCall className="w-4 h-4 mr-1" /> Voice Calls
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="data-[state=active]:bg-white">
              <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
            </TabsTrigger>
            <TabsTrigger value="recordings" className="data-[state=active]:bg-white">
              <Headphones className="w-4 h-4 mr-1" /> Recordings
            </TabsTrigger>
            <TabsTrigger value="actions" className="data-[state=active]:bg-white">
              <Play className="w-4 h-4 mr-1" /> Actions
            </TabsTrigger>
          </TabsList>

          {/* Activity Tab - Detailed Drill Down */}
          <TabsContent value="activity">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">AI Processing Activity</CardTitle>
                <CardDescription>Complete view of leads processed by AI with all actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {detailedActivity.map((lead, index) => (
                    <div 
                      key={index} 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => viewLeadHistory(lead.lead_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-medium">
                            {lead.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium">{lead.name || 'Unknown'}</p>
                            <p className="text-sm text-gray-500">
                              {formatPhone(lead.phone)} • {lead.source}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* Action Indicators */}
                          <div className="flex items-center gap-2">
                            {lead.ai_whatsapp_greeting_sent && (
                              <div className="flex items-center gap-1 text-green-600" title="WhatsApp Sent">
                                <MessageCircle className="w-4 h-4" />
                              </div>
                            )}
                            {lead.call_info && (
                              <div className={`flex items-center gap-1 ${lead.call_info.status === 'triggered' ? 'text-orange-600' : lead.call_info.status === 'completed' ? 'text-green-600' : 'text-purple-600'}`} title={`Call ${lead.call_info.status}`}>
                                <PhoneCall className="w-4 h-4" />
                              </div>
                            )}
                            {lead.recording_info && (
                              <div className="flex items-center gap-1 text-emerald-600" title="Recording Available">
                                <Headphones className="w-4 h-4" />
                              </div>
                            )}
                            {lead.ai_last_call_transcript && (
                              <div className="flex items-center gap-1 text-blue-600" title="Transcript Available">
                                <FileText className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            {getStatusBadge(lead.call_info?.status || lead.ai_followup_status)}
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDateTime(lead.ai_processing_started)}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Quick Summary Row */}
                      {(lead.ai_last_call_insights?.summary || lead.disposition) && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-gray-600">
                            {lead.ai_last_call_insights?.summary || `Disposition: ${lead.disposition}`}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  {detailedActivity.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Bot className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No AI activity yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Voice Calls Tab */}
          <TabsContent value="calls">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Voice Call Logs</CardTitle>
                <CardDescription>Click on any call to see full details, transcript & insights</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {callLogs.map((call, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
                      onClick={() => viewCallDetails(call)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          call.status === 'completed' ? 'bg-green-100' : 
                          call.status === 'triggered' ? 'bg-orange-100' : 
                          call.status === 'failed' ? 'bg-red-100' : 'bg-purple-100'
                        }`}>
                          <PhoneCall className={`w-5 h-5 ${
                            call.status === 'completed' ? 'text-green-600' : 
                            call.status === 'triggered' ? 'text-orange-600' : 
                            call.status === 'failed' ? 'text-red-600' : 'text-purple-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium">{call.lead_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{call.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {call.call_id && (
                          <span className="text-xs text-gray-400 font-mono">{call.call_id.slice(0, 8)}...</span>
                        )}
                        <div className="text-right">
                          {getStatusBadge(call.status)}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDateTime(call.triggered_at || call.scheduled_time || call.created_at)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteScheduledCall(call.call_id || call.lead_id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                  {callLogs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <PhoneCall className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No voice calls yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">WhatsApp Messages</CardTitle>
                <CardDescription>Click on any conversation to see full chat history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {whatsappLogs.map((log, index) => (
                    <div 
                      key={index} 
                      className="p-4 border rounded-lg hover:bg-green-50 cursor-pointer transition-colors"
                      onClick={() => viewMessageDetails(log)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">{log.name || 'Unknown'}</p>
                            <p className="text-sm text-gray-500">{formatPhone(log.phone)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <Badge className="bg-green-500 text-white text-xs">
                              {log.message_count || 1} messages
                            </Badge>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDateTime(log.timestamp)}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Show conversation preview */}
                      {log.conversation && log.conversation.length > 0 && (
                        <div className="mt-2 space-y-2 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                          {log.conversation.slice(0, 3).map((msg, msgIdx) => (
                            <div key={msgIdx} className={`text-sm p-2 rounded ${
                              msg.role === 'assistant' 
                                ? 'bg-green-100 text-green-800 ml-0 mr-8' 
                                : 'bg-blue-100 text-blue-800 ml-8 mr-0'
                            }`}>
                              <span className="font-medium text-xs block mb-1">
                                {msg.role === 'assistant' ? '🤖 AI (Priya)' : '👤 Customer'}
                              </span>
                              <span className="whitespace-pre-wrap">{msg.content?.substring(0, 150)}{msg.content?.length > 150 ? '...' : ''}</span>
                            </div>
                          ))}
                          {log.conversation.length > 3 && (
                            <p className="text-xs text-gray-500 text-center">
                              + {log.conversation.length - 3} more messages
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show last message if no full conversation */}
                      {(!log.conversation || log.conversation.length === 0) && log.last_message && (
                        <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                          <span className="font-medium">Last: </span>
                          {log.last_message.content}
                        </div>
                      )}
                    </div>
                  ))}
                  {whatsappLogs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No WhatsApp messages yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recordings">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Call Recordings & Transcripts</CardTitle>
                <CardDescription>AI-processed call recordings with transcriptions and insights</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recordings.map((rec, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Headphones className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Call Recording</p>
                            <p className="text-sm text-gray-500">Lead: {rec.lead_id?.slice(0, 12)}...</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {rec.recording_url && (
                            <a 
                              href={rec.recording_url}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm hover:bg-emerald-200"
                            >
                              <Volume2 className="w-4 h-4" /> Play Recording
                            </a>
                          )}
                          <span className="text-xs text-gray-400">{formatDateTime(rec.created_at)}</span>
                        </div>
                      </div>
                      
                      {rec.transcript && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Transcript
                          </p>
                          <div className="text-sm text-gray-700">
                            {Array.isArray(rec.transcript) ? (
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {rec.transcript.map((item, idx) => (
                                  <div key={idx} className={`p-2 rounded ${item.speaker === 'agent' ? 'bg-purple-50' : 'bg-gray-100'}`}>
                                    <span className={`text-xs font-medium ${item.speaker === 'agent' ? 'text-purple-600' : 'text-gray-600'}`}>
                                      {item.speaker === 'agent' ? 'Priya (AI)' : 'Customer'}:
                                    </span>
                                    <span className="ml-2">{item.text}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="line-clamp-3">{rec.transcript}</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {(rec.ai_insights || rec.huskyvoice_analytics) && (() => {
                        const insights = parseAIInsights(rec.ai_insights, rec.huskyvoice_analytics);
                        return (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                            <p className="text-xs font-medium text-purple-600 mb-2 flex items-center gap-1">
                              <Zap className="w-3 h-3" /> AI Insights
                            </p>
                            {insights.summary && (
                              <p className="text-sm text-gray-700 mb-2">{insights.summary}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {insights.sentiment && (
                                <Badge variant="outline" className={
                                  insights.sentiment === 'positive' ? 'border-green-300 text-green-700' :
                                  insights.sentiment === 'negative' ? 'border-red-300 text-red-700' :
                                  'border-gray-300 text-gray-700'
                                }>
                                  {insights.sentiment}
                                </Badge>
                              )}
                              {insights.interestLevel && (
                                <Badge variant="outline" className={
                                  insights.interestLevel?.includes('interested') ? 'border-blue-300 text-blue-700' :
                                  'border-orange-300 text-orange-700'
                                }>
                                  {insights.interestLevel}
                                </Badge>
                              )}
                              {rec.huskyvoice_analytics?.appointment_likelihood && (
                                <Badge variant="outline" className="border-purple-300 text-purple-700">
                                  Appointment: {rec.huskyvoice_analytics.appointment_likelihood}
                                </Badge>
                              )}
                            </div>
                            {insights.nextSteps && (
                              <p className="text-xs text-gray-600 mt-2">
                                <strong>Next:</strong> {insights.nextSteps}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                  {recordings.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Headphones className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No recordings yet</p>
                      <p className="text-sm mt-1">Recordings will appear here after AI calls are completed</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Manual Trigger */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Manual Trigger</CardTitle>
                  <CardDescription>Manually trigger AI follow-up for a specific lead</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter Lead ID (e.g., lead_abc123)"
                      value={triggerLeadId}
                      onChange={(e) => setTriggerLeadId(e.target.value)}
                    />
                    <Button onClick={triggerFollowup} disabled={isProcessing || !triggerLeadId}>
                      {isProcessing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      Trigger
                    </Button>
                  </div>
                  {triggerResult && (
                    <div className={`p-3 rounded-lg text-sm ${triggerResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                      <pre className="whitespace-pre-wrap">{JSON.stringify(triggerResult, null, 2)}</pre>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Cleanup & Maintenance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Maintenance</CardTitle>
                  <CardDescription>System maintenance and data cleanup</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-3">
                    <Button 
                      variant="outline" 
                      onClick={syncHuskyVoice} 
                      disabled={isProcessing}
                      className="w-full justify-start"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                      Sync HuskyVoice Recordings
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={transcribeAllRecordings} 
                      disabled={isProcessing}
                      className="w-full justify-start text-purple-600 hover:text-purple-700"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                      Transcribe All Recordings (Whisper AI)
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={updateStaleCalls} 
                      disabled={isProcessing}
                      className="w-full justify-start"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                      Update Stale Triggered Calls
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={cleanupOldCalls} 
                      disabled={isProcessing}
                      className="w-full justify-start text-orange-600 hover:text-orange-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Old Scheduled Calls (7+ days)
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={cleanupTestData} 
                      disabled={isProcessing}
                      className="w-full justify-start"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clean Up Test Data
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Test data cleanup removes leads with phone numbers starting with 99999
                  </p>
                </CardContent>
              </Card>

              {/* System Components */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">System Components Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {systemStatus && Object.entries(systemStatus.components || {}).map(([component, active]) => (
                      <div key={component} className={`p-3 rounded-lg ${active ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center gap-2">
                          {active ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span className="capitalize text-sm font-medium">{component.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Lead History Modal */}
        <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                AI Interaction History
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              {leadHistory && (
                <div className="space-y-6 p-2">
                  {/* Lead Info */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-2">Lead Information</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-gray-500">Name:</span> {leadHistory.lead?.name}</p>
                      <p><span className="text-gray-500">Phone:</span> {leadHistory.lead?.phone}</p>
                      <p><span className="text-gray-500">Source:</span> {leadHistory.lead?.source}</p>
                      <p><span className="text-gray-500">Disposition:</span> {leadHistory.lead?.disposition || '-'}</p>
                    </div>
                  </div>

                  {/* AI Summary */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold mb-2 text-purple-800">AI Processing Summary</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-gray-500">Started:</span> {formatDateTime(leadHistory.ai_summary?.processing_started)}</p>
                      <p><span className="text-gray-500">WhatsApp Sent:</span> {leadHistory.ai_summary?.whatsapp_sent ? 'Yes' : 'No'}</p>
                      <p><span className="text-gray-500">Voice Call:</span> {formatDateTime(leadHistory.ai_summary?.last_call) || 'Not yet'}</p>
                      <p><span className="text-gray-500">Call Duration:</span> {leadHistory.ai_summary?.call_duration ? `${leadHistory.ai_summary.call_duration}s` : '-'}</p>
                      <p><span className="text-gray-500">Has Transcript:</span> {leadHistory.ai_summary?.has_transcript ? 'Yes' : 'No'}</p>
                      <p><span className="text-gray-500">Has Insights:</span> {leadHistory.ai_summary?.has_insights ? 'Yes' : 'No'}</p>
                    </div>
                  </div>

                  {/* Call History */}
                  {leadHistory.calls?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Call History</h3>
                      <div className="space-y-2">
                        {leadHistory.calls.map((call, idx) => (
                          <div key={idx} className="p-3 border rounded-lg">
                            <div className="flex justify-between">
                              <span>{getStatusBadge(call.status)}</span>
                              <span className="text-xs text-gray-500">{formatDateTime(call.created_at)}</span>
                            </div>
                            {call.call_id && <p className="text-xs text-gray-400 mt-1">ID: {call.call_id}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recordings */}
                  {leadHistory.recordings?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Recordings & Transcripts</h3>
                      {leadHistory.recordings.map((rec, idx) => (
                        <div key={idx} className="p-4 border rounded-lg space-y-3">
                          {rec.recording_url && (
                            <a 
                              href={rec.recording_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm hover:bg-emerald-200"
                            >
                              <Volume2 className="w-4 h-4" /> Play Recording
                            </a>
                          )}
                          {rec.transcript && (
                            <div className="p-3 bg-gray-50 rounded">
                              <p className="text-xs font-medium text-gray-500 mb-1">Transcript</p>
                              <div className="text-sm">
                                {Array.isArray(rec.transcript) ? (
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {rec.transcript.map((item, i) => (
                                      <div key={i} className={`p-2 rounded ${item.speaker === 'agent' ? 'bg-purple-50' : 'bg-gray-100'}`}>
                                        <span className={`text-xs font-medium ${item.speaker === 'agent' ? 'text-purple-600' : 'text-gray-600'}`}>
                                          {item.speaker === 'agent' ? 'Priya (AI)' : 'Customer'}:
                                        </span>
                                        <span className="ml-2">{item.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p>{rec.transcript}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {rec.ai_insights && (
                            <div className="p-3 bg-purple-50 rounded">
                              <p className="text-xs font-medium text-purple-600 mb-1">AI Insights</p>
                              <p className="text-sm">{rec.ai_insights.summary}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Call Details Drill-Down Modal */}
        <Dialog open={showCallModal} onOpenChange={setShowCallModal}>
          <DialogContent className="max-w-3xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-purple-600" />
                Call Details
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh]">
              {selectedCall && (
                <div className="space-y-6 p-2">
                  {/* Call Info */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold mb-3 text-purple-800">Call Information</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <p><span className="text-gray-500">Lead Name:</span> {selectedCall.lead_name || 'Unknown'}</p>
                      <p><span className="text-gray-500">Phone:</span> {selectedCall.phone}</p>
                      <p><span className="text-gray-500">Status:</span> {getStatusBadge(selectedCall.status)}</p>
                      <p><span className="text-gray-500">Call ID:</span> <span className="font-mono text-xs">{selectedCall.call_id || '-'}</span></p>
                      <p><span className="text-gray-500">Scheduled:</span> {formatDateTime(selectedCall.scheduled_time)}</p>
                      <p><span className="text-gray-500">Triggered:</span> {formatDateTime(selectedCall.triggered_at)}</p>
                    </div>
                  </div>

                  {/* Recording */}
                  {selectedCall.fullHistory?.recordings?.[0]?.recording_url ? (
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-emerald-800 flex items-center gap-2">
                        <Headphones className="w-4 h-4" /> Recording
                      </h3>
                      <a 
                        href={selectedCall.fullHistory.recordings[0].recording_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        <Volume2 className="w-4 h-4" /> Play Recording
                      </a>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                      <h3 className="font-semibold mb-2 text-gray-600 flex items-center gap-2">
                        <Headphones className="w-4 h-4" /> Recording
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedCall.status === 'triggered' || selectedCall.status === 'scheduled' 
                          ? '⏳ Waiting for call to complete...' 
                          : '⚠️ No recording received from HuskyVoice'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Recording appears here after the call ends and HuskyVoice sends the webhook.
                      </p>
                    </div>
                  )}

                  {/* Transcript */}
                  {(selectedCall.fullHistory?.recordings?.[0]?.transcript || selectedCall.fullHistory?.lead?.ai_last_call_transcript) ? (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-gray-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Call Transcript
                      </h3>
                      <div className="bg-white p-4 rounded border max-h-64 overflow-y-auto">
                        {(() => {
                          const transcript = selectedCall.fullHistory?.recordings?.[0]?.transcript || 
                                           selectedCall.fullHistory?.lead?.ai_last_call_transcript;
                          if (Array.isArray(transcript)) {
                            return (
                              <div className="space-y-2">
                                {transcript.map((item, idx) => (
                                  <div key={idx} className={`p-2 rounded ${item.speaker === 'agent' ? 'bg-purple-50' : 'bg-gray-100'}`}>
                                    <span className={`text-xs font-medium ${item.speaker === 'agent' ? 'text-purple-600' : 'text-gray-600'}`}>
                                      {item.speaker === 'agent' ? 'Priya (AI)' : 'Customer'}:
                                    </span>
                                    <span className="ml-2 text-sm">{item.text}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return <p className="text-sm whitespace-pre-wrap">{transcript}</p>;
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                      <h3 className="font-semibold mb-2 text-gray-600 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Call Transcript
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedCall.status === 'triggered' || selectedCall.status === 'scheduled' 
                          ? '⏳ Transcript will be generated after call completes...' 
                          : '⚠️ Transcript not yet generated (waiting for recording)'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Whisper AI transcribes the recording automatically.
                      </p>
                    </div>
                  )}

                  {/* AI Insights */}
                  {(selectedCall.fullHistory?.recordings?.[0]?.ai_insights || selectedCall.fullHistory?.lead?.ai_last_call_insights) ? (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-blue-800 flex items-center gap-2">
                        <Zap className="w-4 h-4" /> AI Analysis & Insights
                      </h3>
                      {(() => {
                        const insights = selectedCall.fullHistory?.recordings?.[0]?.ai_insights || 
                                        selectedCall.fullHistory?.lead?.ai_last_call_insights;
                        return (
                          <div className="space-y-4">
                            {insights.call_summary && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Summary</p>
                                <p className="text-sm">{insights.call_summary}</p>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              {insights.customer_sentiment && (
                                <p><span className="text-gray-500">Sentiment:</span> 
                                  <Badge className={`ml-2 ${
                                    insights.customer_sentiment === 'positive' ? 'bg-green-500' :
                                    insights.customer_sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-500'
                                  } text-white`}>
                                    {insights.customer_sentiment}
                                  </Badge>
                                </p>
                              )}
                              {insights.interest_level && (
                                <p><span className="text-gray-500">Interest:</span> 
                                  <Badge className={`ml-2 ${
                                    insights.interest_level === 'hot' ? 'bg-red-500' :
                                    insights.interest_level === 'warm' ? 'bg-orange-500' :
                                    insights.interest_level === 'cold' ? 'bg-blue-500' : 'bg-gray-500'
                                  } text-white`}>
                                    {insights.interest_level}
                                  </Badge>
                                </p>
                              )}
                              {insights.appointment_confirmed !== undefined && (
                                <p><span className="text-gray-500">Appointment:</span> 
                                  {insights.appointment_confirmed ? 
                                    <Badge className="ml-2 bg-green-500 text-white">Booked</Badge> :
                                    <Badge className="ml-2 bg-gray-400 text-white">Not Booked</Badge>
                                  }
                                </p>
                              )}
                              {insights.budget_range && (
                                <p><span className="text-gray-500">Budget:</span> {insights.budget_range}</p>
                              )}
                            </div>
                            {insights.customer_objections?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Objections/Concerns</p>
                                <ul className="list-disc list-inside text-sm">
                                  {insights.customer_objections.map((obj, i) => (
                                    <li key={i}>{obj}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {insights.next_best_action && (
                              <div className="p-3 bg-white rounded border">
                                <p className="text-xs font-medium text-gray-500 mb-1">Recommended Next Action</p>
                                <p className="text-sm font-medium">{insights.next_best_action}</p>
                              </div>
                            )}
                            {insights.recommended_disposition && (
                              <p><span className="text-gray-500">Recommended Disposition:</span> 
                                <Badge className="ml-2 bg-purple-500 text-white">{insights.recommended_disposition}</Badge>
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                      <h3 className="font-semibold mb-2 text-gray-600 flex items-center gap-2">
                        <Zap className="w-4 h-4" /> AI Analysis & Insights
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedCall.status === 'triggered' || selectedCall.status === 'scheduled' 
                          ? '⏳ AI insights will be generated after call completes...' 
                          : '⚠️ Insights not yet generated (waiting for transcript)'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        GPT analyzes the transcript to extract: sentiment, interest level, objections, next actions, and recommended disposition.
                      </p>
                    </div>
                  )}

                  {/* Lead Status Updates */}
                  {selectedCall.fullHistory?.lead && (
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-yellow-800">Lead Status (Auto-Updated)</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <p><span className="text-gray-500">Current Disposition:</span> {selectedCall.fullHistory.lead.disposition || '-'}</p>
                        <p><span className="text-gray-500">AI Status:</span> {selectedCall.fullHistory.lead.ai_followup_status || '-'}</p>
                        <p><span className="text-gray-500">Next Follow-up:</span> {selectedCall.fullHistory.lead.next_followup_date || '-'}</p>
                        <p><span className="text-gray-500">Follow-up Reason:</span> {selectedCall.fullHistory.lead.next_followup_reason || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Message Drill-Down Modal */}
        <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                WhatsApp Message Details
              </DialogTitle>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-4 p-2">
                {/* Message Info */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold mb-3 text-green-800">Message Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <p><span className="text-gray-500">Lead Name:</span> {selectedMessage.name || selectedMessage.leadInfo?.name || 'Unknown'}</p>
                    <p><span className="text-gray-500">Phone:</span> {selectedMessage.phone || selectedMessage.leadInfo?.phone || '-'}</p>
                    <p><span className="text-gray-500">Type:</span> <Badge className="bg-green-500 text-white">{selectedMessage.type || 'ai_greeting'}</Badge></p>
                    <p><span className="text-gray-500">Sent At:</span> {formatDateTime(selectedMessage.timestamp)}</p>
                    <p><span className="text-gray-500">Status:</span> <Badge className="bg-green-500 text-white">Delivered</Badge></p>
                    <p><span className="text-gray-500">Lead ID:</span> <span className="font-mono text-xs">{selectedMessage.lead_id || '-'}</span></p>
                  </div>
                </div>

                {/* Message Content */}
                {selectedMessage.content && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-3 text-gray-800">Message Content</h3>
                    <div className="bg-white p-4 rounded border">
                      <p className="text-sm whitespace-pre-wrap">{selectedMessage.content}</p>
                    </div>
                  </div>
                )}

                {/* Lead Info if available */}
                {selectedMessage.leadInfo && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold mb-3 text-blue-800">Lead Information</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <p><span className="text-gray-500">Source:</span> {selectedMessage.leadInfo.source || '-'}</p>
                      <p><span className="text-gray-500">Disposition:</span> {selectedMessage.leadInfo.disposition || '-'}</p>
                      <p><span className="text-gray-500">City:</span> {selectedMessage.leadInfo.city || '-'}</p>
                      <p><span className="text-gray-500">Created:</span> {formatDateTime(selectedMessage.leadInfo.created_at)}</p>
                    </div>
                  </div>
                )}

                {/* View Full Lead Button */}
                {selectedMessage.lead_id && (
                  <Button 
                    className="w-full"
                    onClick={() => {
                      setShowMessageModal(false);
                      viewLeadHistory(selectedMessage.lead_id);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" /> View Full Lead History
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
